// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 32-knowledge.md
import * as React from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { KnowledgeEntryEditor } from '@/components/knowledge/KnowledgeEntryEditor';
import { canMutateScope, formatKnowledgeDate } from '@/lib/knowledgeUtils';
import { api } from '@/lib/api';
import type { IntegrationListItem } from '@greenlight/shared';

export interface IntegrationKnowledgeSheetProps {
  /** Integration whose knowledge is being authored, or null when closed. */
  integration: IntegrationListItem | null;
  userRole: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Integration-scope Knowledge is authored on the integration it describes, not
 * re-listed under /knowledge (docs/28 § /integrations → Edit integration
 * Knowledge in place). Lists this integration's entries, creates new ones, and
 * opens the shared editor.
 */
export function IntegrationKnowledgeSheet({
  integration,
  userRole,
  open,
  onOpenChange,
}: IntegrationKnowledgeSheetProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const createMutation = api.knowledge.createEntry.useMutation();
  const [openEntryId, setOpenEntryId] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [draft, setDraft] = React.useState({ topic: '', title: '', bodyMd: '' });

  const integrationId = integration?.id ?? '';
  const listKey = ['knowledge-entries', 'integration', integrationId];
  const { data, isLoading, isError } = api.knowledge.listEntries.useQuery(
    listKey,
    { query: { scope: 'integration', integrationId } },
    { queryKey: listKey, enabled: open && integrationId !== '' },
  );
  const entries = data?.status === 200 ? data.body.items : [];
  const canEdit = canMutateScope(userRole, 'integration');

  React.useEffect(() => {
    if (!open) {
      setCreating(false);
      setDraft({ topic: '', title: '', bodyMd: '' });
    }
  }, [open]);

  async function onCreate(): Promise<void> {
    if (!integration || !draft.topic.trim() || !draft.title.trim()) return;
    try {
      const result = await createMutation.mutateAsync({
        body: {
          scope: 'integration',
          integrationId: integration.id,
          topic: draft.topic.trim(),
          title: draft.title.trim(),
          bodyMd: draft.bodyMd,
        },
      });
      if (result.status >= 300) throw result;
      toast.success('Knowledge entry created.');
      setCreating(false);
      setDraft({ topic: '', title: '', bodyMd: '' });
      queryClient.invalidateQueries({ queryKey: ['knowledge-entries'] });
      setOpenEntryId(result.body.id);
    } catch {
      // Network/unexpected failure throws before the status check — surface it
      // and leave the form open so the draft isn't lost (matches the dialogs).
      toast.error('Failed to create entry.');
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-4 sm:max-w-xl"
          data-testid="integration-knowledge-sheet"
        >
          <SheetHeader>
            <SheetTitle>{integration ? `${integration.name} · Knowledge` : 'Knowledge'}</SheetTitle>
            <SheetDescription>
              Prose agents read when working with this integration. Authored here, not on the
              Knowledge page.
            </SheetDescription>
          </SheetHeader>

          {canEdit && (
            <div>
              {creating ? (
                <div className="space-y-2 rounded-gl border border-border bg-muted/30 p-3">
                  <Label htmlFor="ik-topic" className="text-xs">
                    Topic
                  </Label>
                  <Input
                    id="ik-topic"
                    value={draft.topic}
                    onChange={(e) => setDraft((d) => ({ ...d, topic: e.target.value }))}
                    placeholder="e.g. rate-limits"
                    className="font-mono"
                  />
                  <Label htmlFor="ik-title" className="text-xs">
                    Title
                  </Label>
                  <Input
                    id="ik-title"
                    value={draft.title}
                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                    placeholder="Short title"
                  />
                  <Label htmlFor="ik-body" className="text-xs">
                    Body (Markdown)
                  </Label>
                  <Textarea
                    id="ik-body"
                    value={draft.bodyMd}
                    onChange={(e) => setDraft((d) => ({ ...d, bodyMd: e.target.value }))}
                    rows={6}
                    className="font-mono"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCreating(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={onCreate}
                      disabled={
                        createMutation.isPending || !draft.topic.trim() || !draft.title.trim()
                      }
                    >
                      Create entry
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => setCreating(true)}
                  data-testid="integration-knowledge-new"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  New entry
                </Button>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <Alert variant="destructive" data-testid="integration-knowledge-error">
              <AlertDescription>
                Couldn't load knowledge entries. Retry in a moment.
              </AlertDescription>
            </Alert>
          ) : entries.length === 0 ? (
            <p
              className="rounded-gl border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
              data-testid="integration-knowledge-empty"
            >
              No knowledge yet for this integration. {canEdit ? 'Add the first entry above.' : ''}
            </p>
          ) : (
            <ul
              className="divide-y divide-border rounded-gl border border-border"
              data-testid="integration-knowledge-list"
            >
              {entries.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col gap-0.5 px-4 py-3 text-left hover:bg-muted/40 focus-visible:bg-muted/60 focus-visible:outline-none"
                    onClick={() => setOpenEntryId(entry.id)}
                  >
                    <span className="text-sm font-medium text-foreground">{entry.title}</span>
                    <span className="text-xs text-muted-foreground">
                      <code>{entry.topic}</code> · v{entry.version} ·{' '}
                      {formatKnowledgeDate(entry.updatedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </SheetContent>
      </Sheet>

      <KnowledgeEntryEditor
        entryId={openEntryId}
        userRole={userRole}
        onClose={() => setOpenEntryId(null)}
      />
    </>
  );
}
